import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Drawer,
  Link,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import PolicyDetailsStyles from "./PolicyDetailsStyles";
import PolicyTab from "./policyTabComponents/PolicyTab";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
  IGetPolicyDetailsHeaderRequestParams,
  IPolicyDetailsHeaderResponse,
} from "../../models/user/policyDetails/PolicyDetails";
import { getBufferData } from "../../utils/Utility";
import Payment from "../Payment/Payment";
import { setDownloadIdCardContent } from "../Dashboard/DashboardSlice";
import CustomDialog from "../../common/CustomDialog/CustomDialog";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import PolicyDetailsHeader from "./PolicyDetailsHeader/PolicyDetailsHeader";
import {
  GetPolicyDetailsHeader,
  GetPolicyRisks,
} from "../../store/user/policyDetails/policyDetailsActions";
import { resetCommonSlice } from "../../store/common/CommonSlice";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import CustomDrawer from "../../common/CustomDrawer/CustomDrawer";
import CustomButton from "../../common/CustomButton/CustomButton";
import Drawers from "../../Agents/Drawers/Drawers";
import Notes from "../../Agents/Notes/Notes";
import { NOTES } from "../../constants";
import { personData } from "../../constants/myTasksPersonData";
import { personChat } from "../../constants/myTasksPersonChatData";
import { PostCustomerDetails } from "../../Agents/CustomerDetailsList/CustomerDetailsListActions";
import ClaimFlow from "../../Agents/ClaimFlow/ClaimFlow";

const PolicyDetails: React.FC = () => {
  const [notesOpen, setNotesOpen] = useState(false);
  const [openPaymentForm, setOpenPaymentForm] = useState<boolean>(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showClaimFlow, setShowClaimFlow] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const pathArray = location.pathname.split("/");

  const { t } = useTranslation();

  const { policyId, carrier, system, lobCode, customerId } = useParams();

  const { policyDetailsHeader, policyVehicles } = useAppSelector(
    (store: RootState) => store.policyDetails
  );
  const { customerDetails } = useAppSelector(
    (state: RootState) => state.customerPolicies
  );

  useEffect(() => {
    if (pathArray[1] === "agent" && customerId?.trim() && carrier && system) {
      dispatch(
        PostCustomerDetails({
          customerId: customerId,
          carrier: carrier,
          system: system,
        })
      );
    }
  }, [dispatch]);
  
  let dueAmount = "0.00";
  let carrierPolicyNumber = "No Policy";
  if (policyDetailsHeader && policyDetailsHeader.dueAmount) {
    dueAmount = policyDetailsHeader.dueAmount;
  }
  if (policyDetailsHeader && policyDetailsHeader.carrierPolicyNumber) {
    carrierPolicyNumber = policyDetailsHeader.carrierPolicyNumber;
  }

  const { downloadIdCardContent } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const { isSuccess, calledReducerType, isError } = useAppSelector(
    (store: RootState) => store.common
  );

  const handlePayment = () => setOpenPaymentForm(true);

  const handlePaymentDialogeClose = () => setOpenPaymentForm(false);

  const handleNavigate = useCallback(() => {
    if (pathArray[1] === "customer") {
      navigate("/customer/dashboard");
    } else if (pathArray[1] === "agent")
        navigate(-1);
  }, [navigate]);
  useEffect(() => {
    window.scrollTo({
      top: 0,
    });
  }, []);
  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      const requestParams: IGetPolicyDetailsHeaderRequestParams = {
        policyId: policyId,
        carrier,
        system,
        lobCode,
      };
      dispatch(GetPolicyDetailsHeader(requestParams));
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  useEffect(() => {
    if (
      isSuccess &&
      calledReducerType === "GetDownloadIdCard" &&
      downloadIdCardContent
    ) {
      const bufferArray = getBufferData(downloadIdCardContent);
      const url = URL.createObjectURL(
        new Blob([bufferArray], { type: "application/pdf" })
      );
      const downloadLink = document.createElement("a");
      downloadLink.download = "Insured ID card.pdf";
      downloadLink.href = url;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      dispatch(setDownloadIdCardContent(null));
    }
  }, [calledReducerType, isSuccess, downloadIdCardContent, dispatch]);

  useEffect(() => {
    if (isError && calledReducerType === "GetDownloadIdCard") {
      alert(`Error in downloading document`);
      dispatch(resetCommonSlice());
    }
  }, [calledReducerType, isError, dispatch]);

  const handleNotesClick = () => {
    setNotesOpen(!notesOpen);
  };

  const handleClaimFlowClick = () => {
    if (policyDetailsHeader && 
      policyDetailsHeader.riskCount !== undefined &&
      policyDetailsHeader.riskCount > 0) {
      setShowClaimFlow(true);
    } else {
      alert("No risks found for this policy");
    }
  };

  return (
    <>
      {showClaimFlow ? (
        <ClaimFlow
          onBack={() => setShowClaimFlow(false)}
          onSubmit={() => setShowClaimFlow(false)}
          customerDetails={customerDetails}
          policyDetailsHeader={policyDetailsHeader}
          policyVehicles={policyVehicles}
        />
      ) : (
        <Box
          id="policydetails_container"
          data-test-id="test-policydetails_container"
          aria-label="Policy Details Container"
          sx={PolicyDetailsStyles.policyDetailsContainer}
        >
          <Box
            id="policydetails_header"
            data-test-id="test-policydetails_header"
            aria-label="Policy Details Header"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              id="policydetails_navigation"
              data-test-id="test-policydetails_navigation"
              aria-label="Policy Details Navigation"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <Link
                id="policydetails_back-link"
                data-test-id="test-policydetails_back-link"
                aria-label="Back to Dashboard"
                onClick={handleNavigate}
                sx={PolicyDetailsStyles.link}
              >
                <Tooltip
                  id="policydetails_back-tooltip"
                  data-test-id="test-policydetails_back-tooltip"
                  aria-label="Go to Dashboard Tooltip"
                  title="Go to Dashboard"
                >
                  <KeyboardArrowLeftIcon />
                </Tooltip>
              </Link>
              <Typography
                id="policydetails_title"
                data-test-id="test-policydetails_title"
                aria-label="Policy Details Title"
              >
                {policyDetailsHeader
                  ? `${policyDetailsHeader.carrierPolicyNumber} | ${
                      policyDetailsHeader?.insuredName
                        ? policyDetailsHeader?.insuredName
                        : policyDetailsHeader?.insuredList?.[0]?.businessName
                    } ${policyDetailsHeader?.insuredNameJoint ? "|" : ""} ${
                      policyDetailsHeader.insuredNameJoint
                        ? policyDetailsHeader.insuredNameJoint
                        : ""
                    }`
                  : ""}
              </Typography>
            </Box>
            {pathArray && pathArray[1] === "agent" && (
              <Box
                display={"flex"}
                gap={2}
                id="policydetails_agent-actions"
                data-test-id="test-policydetails_agent-actions"
                aria-label="Agent Actions"
              >
                <CustomButton
                  type="outlined"
                  text="Add Notes"
                  onClick={() => setNotesOpen(true)}
                  additionalClassName={PolicyDetailsStyles.button}
                />
                {policyDetailsHeader?.lobCode === "PA" && (
                  <CustomButton
                    type="outlined"
                    text="Add FNOL"
                    onClick={handleClaimFlowClick}
                    additionalClassName={PolicyDetailsStyles.button}
                  />
                )}
              </Box>
            )}
          </Box>
          <PolicyDetailsHeader
            data={policyDetailsHeader}
            handlePayment={handlePayment}
            policyVehicles={policyVehicles}
          />
          <PolicyTab />
        </Box>
      )}
      {/* <CustomDialog
        open={openPaymentForm}
        maxWidth="lg"
        onClose={handlePaymentDialogeClose}
        title={`${t("Payment for Policy Number")} : ${carrierPolicyNumber}`}
        content={
          <Payment
            amount={dueAmount}
            carrierPolicyNumber={carrierPolicyNumber}
          />
        }
        showDivider={true}
      /> */}

      {/* <Drawer
        PaperProps={{
          sx: PolicyDetailsStyles.paperPropsStyles,
        }}
        open={openPaymentForm}
        anchor="right"
        onClose={handlePaymentDialogeClose}
      >
        <Box sx={PolicyDetailsStyles.headerStyles}>
          <Typography variant="h6" sx={PolicyDetailsStyles.payNowHeadingStyle}>
            {`${t("Payment for Policy Number")} : ${carrierPolicyNumber}`}
          </Typography> */}
      {/* <Button
            sx={{
              boxShadow: "none",
              "&:hover": { background: "unset" },
            }}
            onClick={handlePaymentDialogeClose}
          >
            <IconButton onClick={handlePaymentDialogeClose}>
              <CloseRounded />
            </IconButton>
          </Button> */}
      {/* <CloseIcon
            onClick={handlePaymentDialogeClose}
            sx={{ cursor: "pointer" }}
          />
        </Box>
        <Box sx={PolicyDetailsStyles.paymentCardStyle}>
          <Payment
            amount={dueAmount}
            carrierPolicyNumber={carrierPolicyNumber}
          />
        </Box>
      </Drawer> */}

      <CustomDrawer
        content={
          <Payment
            amount={dueAmount}
            carrierPolicyNumber={carrierPolicyNumber}
          />
        }
        open={openPaymentForm}
        onClose={handlePaymentDialogeClose}
        title={`${t("Payment for Policy Number")} : ${carrierPolicyNumber}`}
      />
      <Drawers open={notesOpen} toggleDrawer={handleNotesClick} heading={NOTES}>
        <Notes
          personDataArray={personData}
          personChatArray={personChat}
          handleNotesClick={handleNotesClick}
          setOpenSnackbar={setOpenSnackbar}
          customerDetails={customerDetails}
        />
      </Drawers>
      <Snackbar
        id="policydetails_snackbar"
        data-test-id="test-policydetails_snackbar"
        aria-label="Notification Snackbar"
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          id="policydetails_success-alert"
          data-test-id="test-policydetails_success-alert"
          aria-label="Success Alert"
          onClose={() => setOpenSnackbar(false)}
          severity="success"
          variant="filled"
        >
          Note saved successfully!
        </Alert>
      </Snackbar>
    </>
  );
};
export default PolicyDetails;